import { api, apiUrl, newIdempotencyKey } from "./api";

export type QueuedMutation = {
  id: string;
  path: string;
  method: "POST" | "PATCH";
  body: unknown;
  headers: Record<string, string>;
  createdAt: string;
  attempts: number;
  lastError?: string;
};

export type QueuedAttachment = {
  id: string;
  ownerType: "work_order" | "customer" | "installment" | "payment" | "expense";
  ownerId: string;
  category: "before_photo" | "after_photo" | "signature" | "expense_proof" | "payment_proof" | "occurrence" | "general";
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  capturedAt: string;
  blob: Blob;
  createdAt: string;
  attempts: number;
  lastError?: string;
};

const DB_NAME = "ja-operations";
const MUTATION_STORE = "mutation-queue";
const ATTACHMENT_STORE = "attachment-queue";

const openDatabase = () => new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, 2);
  request.onupgradeneeded = () => {
    if (!request.result.objectStoreNames.contains(MUTATION_STORE)) request.result.createObjectStore(MUTATION_STORE, { keyPath: "id" });
    if (!request.result.objectStoreNames.contains(ATTACHMENT_STORE)) request.result.createObjectStore(ATTACHMENT_STORE, { keyPath: "id" });
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const run = async <T>(storeName: string, mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>) => {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = operation(transaction.objectStore(storeName));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => database.close();
  });
};

export const listQueuedMutations = () => run<QueuedMutation[]>(MUTATION_STORE, "readonly", (store) => store.getAll());
export const removeQueuedMutation = (id: string) => run(MUTATION_STORE, "readwrite", (store) => store.delete(id));
export const listQueuedAttachments = () => run<QueuedAttachment[]>(ATTACHMENT_STORE, "readonly", (store) => store.getAll());
export const removeQueuedAttachment = (id: string) => run(ATTACHMENT_STORE, "readwrite", (store) => store.delete(id));
export const getQueueStatus = async () => {
  const [mutations, attachments] = await Promise.all([listQueuedMutations(), listQueuedAttachments()]);
  const operations = [...mutations, ...attachments];
  return { total: operations.length, failed: operations.filter(({ lastError }) => Boolean(lastError)).length };
};

export async function mutateOffline<T>(path: string, body: unknown, headers: Record<string, string> = {}): Promise<T | { queued: true; id: string }> {
  const id = newIdempotencyKey();
  const requestHeaders = { "Idempotency-Key": id, ...headers };
  try {
    return await api<T>(path, { method: "POST", body: JSON.stringify(body), headers: requestHeaders });
  } catch (error) {
    if (navigator.onLine) throw error;
    const mutation: QueuedMutation = {
      id, path, method: "POST", body, headers: requestHeaders,
      createdAt: new Date().toISOString(), attempts: 0,
    };
    await run(MUTATION_STORE, "readwrite", (store) => store.put(mutation));
    window.dispatchEvent(new CustomEvent("ja:queue-changed"));
    return { queued: true, id };
  }
}

const fileHash = async (blob: Blob) => {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const sendAttachment = async (attachment: QueuedAttachment) => {
  const intent = await api<{ attachmentId: string }>("/attachments/upload-intents", {
    method: "POST",
    headers: { "Idempotency-Key": attachment.id },
    body: JSON.stringify({
      ownerType: attachment.ownerType,
      ownerId: attachment.ownerId,
      category: attachment.category,
      originalName: attachment.originalName,
      mimeType: attachment.mimeType,
      sizeBytes: attachment.sizeBytes,
      sha256: attachment.sha256,
      capturedAt: attachment.capturedAt,
    }),
  });
  const response = await fetch(apiUrl(`/attachments/${intent.attachmentId}/content`), {
    method: "PUT", credentials: "include", headers: { "Content-Type": attachment.mimeType }, body: attachment.blob,
  });
  if (!response.ok) throw new Error("Não foi possível enviar o arquivo.");
  return { id: intent.attachmentId };
};

export async function uploadAttachmentOffline(input: {
  ownerType: QueuedAttachment["ownerType"];
  ownerId: string;
  category: QueuedAttachment["category"];
  file: File;
  capturedAt?: string;
}): Promise<{ id: string } | { queued: true; id: string }> {
  const queued: QueuedAttachment = {
    id: newIdempotencyKey(),
    ownerType: input.ownerType,
    ownerId: input.ownerId,
    category: input.category,
    originalName: input.file.name,
    mimeType: input.file.type,
    sizeBytes: input.file.size,
    sha256: await fileHash(input.file),
    capturedAt: input.capturedAt ?? new Date().toISOString(),
    blob: input.file,
    createdAt: new Date().toISOString(),
    attempts: 0,
  };
  if (navigator.onLine) {
    try { return await sendAttachment(queued); }
    catch (error) { if (!(error instanceof TypeError) && navigator.onLine) throw error; }
  }
  await run(ATTACHMENT_STORE, "readwrite", (store) => store.put(queued));
  window.dispatchEvent(new CustomEvent("ja:queue-changed"));
  return { queued: true, id: queued.id };
}

export async function replayQueue(): Promise<void> {
  if (!navigator.onLine) return;
  const mutations = (await listQueuedMutations()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const mutation of mutations) {
    try {
      await api(mutation.path, {
        method: mutation.method,
        body: JSON.stringify(mutation.body),
        headers: mutation.headers,
      });
      await removeQueuedMutation(mutation.id);
    } catch (error) {
      if (error instanceof TypeError) break;
      mutation.attempts += 1;
      mutation.lastError = error instanceof Error ? error.message : "Erro desconhecido";
      await run(MUTATION_STORE, "readwrite", (store) => store.put(mutation));
      if (mutation.attempts >= 5) continue;
      break;
    }
  }
  const attachments = (await listQueuedAttachments()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const attachment of attachments) {
    try {
      await sendAttachment(attachment);
      await removeQueuedAttachment(attachment.id);
    } catch (error) {
      if (error instanceof TypeError) break;
      attachment.attempts += 1;
      attachment.lastError = error instanceof Error ? error.message : "Erro desconhecido";
      await run(ATTACHMENT_STORE, "readwrite", (store) => store.put(attachment));
      if (attachment.attempts < 5) break;
    }
  }
  window.dispatchEvent(new CustomEvent("ja:queue-changed"));
}
